/**
 * OEMQ Trend Graph Component
 * Area chart showing OEMQ scores over the past 7 days (including today)
 * Matches the design from the Performance Analysis dashboard
 */

import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './OemqTrendGraph.css';

const OemqTrendGraph = ({ data = [] }) => {
  // Format data for Recharts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((item) => {
      const date = new Date(item.date);
      return {
        date: item.date,
        oemqScore: item.oemqScore || 0,
        formattedDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      };
    });
  }, [data]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="oemq-tooltip">
          <p className="tooltip-label">{data.formattedDate || data.date}</p>
          <p className="tooltip-value">
            <span className="tooltip-label">OEMQ Score:</span>
            <span className="tooltip-number">{data.oemqScore.toFixed(2)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom Y-axis tick formatter
  const formatYAxis = (value) => {
    return value.toFixed(2);
  };

  if (chartData.length === 0) {
    return (
      <div className="oemq-graph-container">
        <div className="oemq-graph-empty">
          <p>No OEMQ score data available</p>
        </div>
      </div>
    );
  }

  // Calculate domain for Y-axis (with some padding)
  const scores = chartData.map((d) => d.oemqScore);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const calculatedPadding = (maxScore - minScore) * 0.1;
  const padding = Math.max(calculatedPadding, 0.5); // Ensure minimum 0.5 units of padding
  const yDomain = [Math.max(0, minScore - padding), maxScore + padding];

  return (
    <div className="oemq-graph-container">
      <div className="oemq-graph-header">
        <h3 className="oemq-graph-title">Performance Analysis</h3>
      </div>

      <div className="oemq-graph-chart-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
          >
            <defs>
              <linearGradient id="oemqGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6148F6" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#6148F6" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="0" 
              stroke="#e5e7eb" 
              vertical={false}
              horizontal={true}
              opacity={0.5}
            />
            <XAxis
              dataKey="formattedDate"
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              label={{ value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 } }}
            />
            <YAxis
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={formatYAxis}
              domain={yDomain}
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="oemqScore"
              stroke="#6148F6"
              strokeWidth={2.5}
              fill="url(#oemqGradient)"
              dot={{ fill: '#6148F6', r: 4, strokeWidth: 2, stroke: '#ffffff' }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: '#ffffff', fill: '#6148F6' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OemqTrendGraph;
